import { useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import IconButton from "@common/components/IconButton";
import { useConfirm } from "@common/components/ConfirmDialog";
import AdminLambdas from "./AdminLambdas";
import AdminLogs from "./AdminLogs";
import AdminVisitors from "./AdminVisitors";
import GameRow from "../library/GameRow";
import { formatBytes } from "@common/format";
import { displayName } from "../emulator/saves";
import { admin, signedInAdmin, type AdminMember } from "../library/admin";
import { useAdminMode } from "@common/adminMode";
import { useAppDispatch, useAppSelector } from "@common/store/hooks";
import { redirectToLogin } from "@common/store/slices/authSlice";
import { forgetCoverIndex, freshCoverIndex, hasCover } from "../library/covers";
import { ago } from "../library/dashboard";
import PlayerLink from "../library/PlayerLink";
import { SYSTEM_LABEL, type System } from "../library/localLibrary";
import { errorText, romChangeKeys, queryKeys, saveKeys, useAdminDeploys, useAdminMembers, useAdminSaves, useCloudRoms } from "../library/queries";

// Consoles the server still recognises but CloudBoy doesn't run.
const RETIRED_LABEL: Record<string, string> = { nds: "Nintendo DS", psx: "PlayStation", psp: "PSP" };

const message = errorText;

function MemberSaves({ member, onError }: { member: AdminMember; onError: (text: string) => void }) {
  const { confirm, dialog: confirmDialog } = useConfirm();
  const queryClient = useQueryClient();
  const query = useAdminSaves(member.sub);
  const saves = query.data ?? null;
  // The dashboard shows each player's last save, so it changes too.
  const load = () =>
    saveKeys(member.sub, member.sub === signedInAdmin().sub).forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));

  if (query.error) return <p className="notice error small">{message(query.error, "Could not load saves.")}</p>;
  if (saves === null) return <p className="muted small">Loading saves…</p>;
  if (saves.length === 0) return <p className="muted small">No cloud saves.</p>;
  return (
    <>
      {confirmDialog}
    <ul className="admin-files">
      {saves.map((save) => (
        <li key={save.name}>
          <span className="admin-file-name">{save.name}</span>
          <span className="muted small">
            {formatBytes(save.size)} · {ago(save.updatedAt)}
          </span>
          <IconButton
            icon="delete"
            label="Delete this save"
            className="danger"
            onClick={async () => {
              if (!(await confirm({ title: `Delete ${save.name}?`, message: `It is removed from ${member.email ?? "this player"}'s cloud saves. This can't be undone.` }))) return;
              try {
                await admin.deleteSave(member.sub, save.name);
                load();
              } catch (e) {
                onError(message(e, "Could not delete the save."));
              }
            }}
          />
        </li>
      ))}
    </ul>
    </>
  );
}

const DEPLOY_LABEL = { ui: "Website", api: "API", terraform: "Infrastructure" } as const;
const formatTime = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function AdminPage() {
  const { confirm, dialog: confirmDialog } = useConfirm();
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading } = useAppSelector((s) => s.auth);
  const { sub: me } = signedInAdmin();
  // The page's data loads only for an admin with admin tools on; the API
  // checks the group on every call regardless.
  const { isAdmin, unlocked, setEnabled: setAdminTools } = useAdminMode();
  const queryClient = useQueryClient();
  const memberList = useAdminMembers(unlocked);
  const members = memberList.data ?? null;
  const romList = useCloudRoms(unlocked);
  const roms = useMemo(
    () => (romList.data ? [...romList.data].sort((a, b) => a.name.localeCompare(b.name)) : null),
    [romList.data],
  );
  const [open, setOpen] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [actionError, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const error =
    actionError ??
    (memberList.error ? message(memberList.error, "Could not load players.") : null) ??
    (romList.error ? message(romList.error, "Could not load the library.") : null);

  const loadMembers = () => queryClient.invalidateQueries({ queryKey: queryKeys.adminMembers });
  // A rename or removal also moves play time, the dashboard and every player's saves.
  const loadRoms = () => Promise.all(romChangeKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));

  // Each action reports its outcome and reloads what it changed.
  const run = async <T,>(work: () => Promise<T>, done: string | ((result: T) => string), reload: () => void) => {
    setError(null);
    setNotice(null);
    try {
      const result = await work();
      setNotice(typeof done === "string" ? done : done(result));
      reload();
    } catch (e) {
      setError(message(e, "That didn't work."));
    }
  };

  const addMember = (e: FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    run(() => admin.addMember(address), `${address} can now use CloudBoy's cloud features.`, () => {
      setEmail("");
      loadMembers();
    });
  };

  // Covers: by default only games without one; `all` fetches every game's
  // again. One game at a time (each call finds and saves one game's art), so
  // a long library can't hit the API's time limit and the cover index is
  // never written by two calls at once.
  const [covers, setCovers] = useState<{ done: number; total: number } | null>(null);
  const syncCovers = async (all: boolean) => {
    if (!roms?.length || covers) return;
    setError(null);
    setNotice(null);
    const index = all ? {} : await freshCoverIndex();
    const games = roms.filter((rom) => !hasCover(rom.name, index));
    if (!games.length) {
      setNotice("Every game in the library has a cover.");
      return;
    }
    let found = 0;
    const missed: string[] = [];
    let failure: unknown = null;
    setCovers({ done: 0, total: games.length });
    for (const [i, rom] of games.entries()) {
      try {
        if ((await admin.syncCover(rom.id)).outcome === "cover") found++;
        else missed.push(displayName(rom.name));
      } catch (e) {
        failure ??= e;
        missed.push(displayName(rom.name));
      }
      setCovers({ done: i + 1, total: games.length });
    }
    forgetCoverIndex();
    setCovers(null);
    if (failure) setError(message(failure, "Some covers couldn't be fetched."));
    setNotice(`Covers: ${found} of ${games.length} found.${missed.length ? ` None for ${missed.join(", ")}.` : ""}`);
  };

  const deploys = useAdminDeploys(unlocked);
  const label = (member: AdminMember) => member.name ?? member.email ?? `Account ${member.sub.slice(0, 8)}`;

  return (
    <div className="about-main admin">
      <h1>Admin</h1>
      {isLoading ? (
        <p className="muted">Checking sign-in…</p>
      ) : !isAuthenticated ? (
        <section className="panel">
          <p>This page is for CloudBoy admins. Sign in to see the admin tools.</p>
          <button type="button" className="btn primary" onClick={() => dispatch(redirectToLogin())}>
            Sign in
          </button>
        </section>
      ) : !isAdmin ? (
        <p className="muted">
          This page is for CloudBoy admins. If you were just made one, sign out and back in so your sign-in picks it
          up.
        </p>
      ) : !unlocked ? (
        <section className="panel">
          <p>Admin tools are off. Turn them on here or in About, Preferences; they stay on for this device.</p>
          <button type="button" className="btn primary" onClick={() => setAdminTools(true)}>
            Turn on admin tools
          </button>
        </section>
      ) : (
        <>
          {error && <p className="notice error">{error}</p>}
          {notice && <p className="notice">{notice}</p>}

          <section className="panel">
            <h2>Deploys</h2>
            {deploys.error ? (
              <p className="notice error">{errorText(deploys.error, "Could not read the deploy times.")}</p>
            ) : (
              <div className="library-stats">
                {(deploys.data ?? []).map((deploy) => (
                  <div key={deploy.area} data-tip={deploy.at ? `${formatTime(deploy.at)}${deploy.by ? ` by ${deploy.by}` : ""}` : undefined}>
                    <span className="muted small">{DEPLOY_LABEL[deploy.area]}</span>
                    <strong>{deploy.at ? ago(deploy.at) : "Never"}</strong>
                    <span className="muted small">{deploy.at ? formatTime(deploy.at) : "No deploy recorded"}</span>
                  </div>
                ))}
                {deploys.isLoading && <span className="muted small">Loading…</span>}
              </div>
            )}
          </section>

          <AdminLambdas />

          <AdminVisitors />

          <AdminLogs />

          <section className="panel">
            <div className="section-head">
              <h2>Players</h2>
            </div>
            <form className="row admin-add" onSubmit={addMember}>
              <input
                className="input"
                type="email"
                placeholder="Add a player by their kast-dev email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Email of the player to add"
              />
              <IconButton icon="add" label="Add player" type="submit" className="primary" />
            </form>
            {members === null ? (
              <p className="muted small">Loading…</p>
            ) : (
              <ul className="game-list">
                {members.map((member) => (
                  <li key={member.sub} className="game-row admin-row">
                    <div className="dashboard-user">
                      <strong>
                        <PlayerLink sub={member.sub} email={member.email} name={member.name} me={null} />
                        {member.name && member.email && <span className="muted small"> {member.email}</span>}
                      </strong>
                      <span className="admin-badges">
                        {member.sub === me && <span className="pill accent">you</span>}
                        {member.admin && <span className="pill accent">admin</span>}
                        {!member.player && <span className="pill">no player access</span>}
                      </span>
                    </div>
                    <div className="btn-group">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => setOpen(open === member.sub ? null : member.sub)}
                        aria-expanded={open === member.sub}
                      >
                        Saves
                      </button>
                      <button
                        type="button"
                        className="btn"
                        disabled={member.sub === me && member.admin}
                        onClick={() =>
                          run(
                            () => admin.setAdmin(member.sub, !member.admin),
                            member.admin ? `${label(member)} is no longer an admin.` : `${label(member)} is now an admin.`,
                            loadMembers,
                          )
                        }
                      >
                        {member.admin ? "Remove admin" : "Make admin"}
                      </button>
                      <IconButton
                        icon="delete"
                        label="Remove from CloudBoy"
                        className="danger"
                        disabled={member.sub === me}
                        onClick={() => {
                          confirm({ title: `Remove ${label(member)}?`, message: "They lose access to CloudBoy's cloud features. Their cloud saves stay.", action: "Remove" }).then(
                            (yes) => {
                              if (yes) run(() => admin.removeMember(member.sub), `${label(member)} was removed.`, loadMembers);
                            },
                          );
                        }}
                      />
                    </div>
                    {open === member.sub && (
                      <div className="admin-saves">
                        <MemberSaves member={member} onError={setError} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="muted small">
              Players need a kast-dev account first (sign up at login.kast-dev.net). Changes reach them the next time
              they sign in.
            </p>
          </section>

          <section className="panel">
            <div className="section-head">
              <h2>Library</h2>
              <span className="btn-group">
              {covers && (
                <span className="muted small">
                  Covers {covers.done} of {covers.total}
                </span>
              )}
              <IconButton
                icon="image"
                label="Get missing covers"
                onClick={() => void syncCovers(false)}
                disabled={!roms?.length || covers !== null}
              />
              <IconButton
                icon="refresh"
                label="Fetch every cover again"
                onClick={() => void syncCovers(true)}
                disabled={!roms?.length || covers !== null}
              />
              </span>
            </div>
            {roms === null ? (
              <p className="muted small">Loading…</p>
            ) : roms.length === 0 ? (
              <p className="muted small">The shared library is empty.</p>
            ) : (
              <ul className="library-list admin-library">
                {roms.map((rom) => (
                  <GameRow
                    key={rom.id}
                    name={rom.name}
                    system={rom.system}
                    meta={[
                      SYSTEM_LABEL[rom.system as System] ?? (RETIRED_LABEL[rom.system] ? `${RETIRED_LABEL[rom.system]} (desktop app)` : "Unknown console"),
                      formatBytes(rom.size),
                      `added ${ago(rom.uploadedAt)}`,
                    ].join(" · ")}
                    actions={
                      <>
                        <IconButton
                          icon="edit"
                          label={`Rename ${rom.name}`}
                          onClick={() => {
                            const name = window.prompt("New file name (keep the extension):", rom.name)?.trim();
                            if (!name || name === rom.name) return;
                            run(
                              () => admin.renameRom(rom.id, name),
                              ({ saves, cover, plays }) =>
                                `Renamed to ${name}. Moved ${saves === 1 ? "1 save" : `${saves} saves`}, ${
                                  plays === 1 ? "1 player's play time" : `${plays} players' play time`
                                }${cover ? " and the cover" : ""}.`,
                              loadRoms,
                            );
                          }}
                        />
                        <IconButton
                          icon="delete"
                          label={`Remove ${rom.name} from the library`}
                          className="danger"
                          onClick={() => {
                            confirm({ title: `Remove ${rom.name}?`, message: "It leaves the shared library for everyone. Saves and play time stay.", action: "Remove" }).then(
                              (yes) => {
                                if (yes) run(() => admin.deleteRom(rom.id), `${rom.name} was removed.`, loadRoms);
                              },
                            );
                          }}
                        />
                      </>
                    }
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      )}
      {confirmDialog}
    </div>
  );
}
